import React, {useMemo} from 'react';
import clsx from 'clsx';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import type {
	CustomFormItemType,
	CustomFormFormValues,
	ICustomFormItem,
} from '../../types';
import {ReactHookFormCustomFormItemCheckboxes} from './ReactHookFormCustomFormItemCheckboxes';
import {ReactHookFormCustomFormItemDate} from './ReactHookFormCustomFormItemDate';
import {ReactHookFormCustomFormItemMultiselect} from './ReactHookFormCustomFormItemMultiselect';
import {ReactHookFormCustomFormItemNumber} from './ReactHookFormCustomFormItemNumber';
import {ReactHookFormCustomFormItemRadios} from './ReactHookFormCustomFormItemRadios';
import {CustomFormItemSection} from './CustomFormItemSection';
import {ReactHookFormCustomFormItemSelect} from './ReactHookFormCustomFormItemSelect';
import {ReactHookFormCustomFormItemText} from './ReactHookFormCustomFormItemText';
import {
	customFormItemClasses,
	getCustomFormItemUtilityClass,
	CUSTOM_FORM_ITEM_CLASS_NAME,
	type CustomFormItemClasses,
} from './customFormItemClasses';
import {UseFormReturn} from 'react-hook-form';

const getItemComponent = <T extends CustomFormItemType>(type: T) => {
	switch (type) {
		case 'checkboxes':
			return ReactHookFormCustomFormItemCheckboxes;
		case 'date':
			return ReactHookFormCustomFormItemDate;
		case 'multiselect':
			return ReactHookFormCustomFormItemMultiselect;
		case 'number':
			return ReactHookFormCustomFormItemNumber;
		case 'radios':
			return ReactHookFormCustomFormItemRadios;
		case 'section':
			return CustomFormItemSection;
		case 'select':
			return ReactHookFormCustomFormItemSelect;
		case 'text':
		case 'textarea':
			return ReactHookFormCustomFormItemText;
	}
};

type CustomFormItemOwnerState = WithClasses<CustomFormItemClasses, {}>;

const useUtilityClasses = (props: CustomFormItemOwnerState) => {
	const {classes} = props;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getCustomFormItemUtilityClass, classes);
};

export const CustomFormItemRoot = styled('div', {
	name: CUSTOM_FORM_ITEM_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	marginTop: theme.spacing(2),
}));

export type CustomFormItemProps<
	TValues extends CustomFormFormValues = CustomFormFormValues,
> = WithClasses<
	CustomFormItemClasses,
	{
		disabled?: boolean;
		error?: string;
		item: ICustomFormItem;
		form: UseFormReturn<TValues>;
	}
>;

export const CustomFormItemPrimitive = <
	TValues extends CustomFormFormValues = CustomFormFormValues,
>({
	classes: _classes,
	className,
	disabled,
	item,
	form,
	...props
}: CustomFormItemProps<TValues>) => {
	const ownerState: CustomFormItemOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const Component = useMemo(() => getItemComponent(item.type), [item.type]);

	if (!Component) return null;

	return (
		<CustomFormItemRoot className={clsx(classes.root, className)}>
			<Component
				control={form.control}
				disabled={disabled}
				item={item}
				{...props}
			/>
		</CustomFormItemRoot>
	);
};

export const CustomFormItem = CustomFormItemPrimitive;

export default CustomFormItem;
